import { useMutation, useQueryClient } from '@tanstack/react-query'

import { toast } from '@ems/ui/components/sonner'

type Fetcher = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>

/**
 * End this session (`POST /api/auth/sign-out`).
 *
 * Better Auth deletes the session row and expires the cookie. A response that is not ok is
 * reported rather than swallowed: telling somebody they are signed out when the session is still
 * alive is the one failure this button must not have on a shared machine.
 */
export async function signOut(doFetch: Fetcher = globalThis.fetch.bind(globalThis)): Promise<void> {
  const response = await doFetch('/api/auth/sign-out', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: '{}',
  })
  if (!response.ok) throw new Error(`Sign-out failed (${response.status}).`)
}

/**
 * Sign out, then leave the console by a full page load.
 *
 * The cache is cleared first and the navigation is a real one rather than a router push: every
 * query here was fetched as the person leaving, and the next person at this browser should not
 * be shown their editions, their members or their name for a single frame.
 */
export function useSignOut() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => signOut(),
    onSuccess: () => {
      queryClient.clear()
      globalThis.location.assign('/login')
    },
    onError: () => {
      toast.error('You are still signed in', {
        description: 'Signing out did not go through. Try again, or close the browser.',
      })
    },
  })
}
